import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { Col } from 'react-bootstrap'
import DisabledHint from '../../common/DisabledHint'
import { readingAge } from './readingAge'

// The current time, ticking every `everyMs` while `enabled`, so a "5 min ago"
// keeps counting while the page is open.
const useNow = (enabled, everyMs = 60000) => {
    const [now, setNow] = useState(() => Date.now())
    useEffect(() => {
        if (!enabled) return undefined
        setNow(Date.now())
        const timer = setInterval(() => setNow(Date.now()), everyMs)
        return () => clearInterval(timer)
    }, [enabled, everyMs])
    return now
}

const DOT = { live: '#31F338', stale: '#F59E0B', none: '#C4C7CC', past: '#8EA2D8' }

// `iconClickable` makes the icon run OnClick too (with `iconTitle` as its
// tooltip), for tiles whose icon is a control, like Appliance Status.
// `disabledHint`, while that control cannot be used, is shown on hovering the
// icon and the value to say why.
// `reading` marks a tile whose value is one meter reading (voltage, power...)
// rather than a total: its footer says when that reading was taken and how
// old it is - or, when `live` is false (a past day), that it was the day's
// last reading.
function DetailContainer({ name, value, time, color, borderColor, image, OnClick = () => { }, styleValue = {}, showDay = false, showTime = true, hasLog = true, textColor, iconClickable = false, iconTitle, disabledHint, reading = false, live = true }) {
    const styles = {
        container: {
            height: '100%',
            borderRadius: '14px',
            background: '#FFFFFF',
            padding: '24px',
        },
        smallContainer: {
            display: 'flex',
            justifyContent: 'space-between',
        },
        leftContainer: {
            borderLeft: `3px solid ${borderColor}`,
            paddingLeft: '17px',
            display: 'flex',
            flexDirection: "column",
            alignItems: 'flex-start',
            gap: '5px',
        },
        name: {
            color: "#A6AAB0",
            fontFamily: "manrope",
            fontSize: "15px",
            fontStyle: "normal",
            fontWeight: 500,
            lineHeight: "24px",
        },
        value: {
            color: "#363B3F",
            fontFamily: "manrope",
            fontSize: "22px",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "24px", /* 96% */
        },
        iconContainer: {
            height: '41px',
            width: '41px',
            borderRadius: "7px",
            background: color,
            padding: "5px",
           

        },
        logText: {
            color: "#A7AAB1",
            fontFamily: "manrope",
            fontSize: "15px",
            fontStyle: "normal",
            fontWeight: 400,
            lineHeight: "24px",
            
        },
        logTime: {
            color: "#2050FF",
            fontFamily: "manrope",
            fontSize: "15px",
            fontStyle: "normal",
            fontWeight: 700,
            lineHeight: "24px",
        }
    }
    // A time of 0 is the "no log" fallback the tiles pass, never a real log -
    // shown as-is it reads as 5:30 AM (the epoch in IST).
    const logFound = hasLog && time !== 0;
    const now = useNow(reading && live && logFound && showTime);
    const age = reading && live && logFound ? readingAge(time, now) : null;
    const shownTime = (withDay) =>
        new Date(time).toLocaleString('en-us', {
            ...(withDay && { day: 'numeric', month: 'short' }),
            hour: 'numeric',
            minute: 'numeric',
        });

    // The footer: dot colour, lead-in, the time in blue, and anything after.
    let footer;
    if (!reading) {
        footer = {
            dot: logFound ? DOT.live : DOT.none,
            text: logFound ? 'Last log' : 'No log found',
            time: logFound ? shownTime(showDay) : null,
        };
    } else if (!logFound) {
        footer = { dot: DOT.none, text: live ? 'No reading yet' : 'No reading that day' };
    } else if (!live) {
        footer = { dot: DOT.past, text: 'Last reading of', time: shownTime(true) };
    } else {
        footer = {
            dot: age.stale ? DOT.stale : DOT.live,
            text: 'Reading at',
            time: shownTime(showDay),
            after: age.text,
            title: age.stale ? 'No newer reading has come in for a while' : undefined,
        };
    }
    return (
        // <Col md={3} sm={12}>
        <div style={{ ...styles.container }}>
            <div style={{ ...styles.smallContainer }}>
                <div style={{ ...styles.leftContainer }}>
                    <div style={{ ...styles.name }}>
                        {name}
                    </div>
                    <div
                        style={{ ...styles.value, ...styleValue , ...(textColor && { color: textColor }), ...(disabledHint && { cursor: 'not-allowed' })}}
                        title={disabledHint || undefined}
                        onClick={() => OnClick()}
                    >
                        {value}
                    </div>
                </div>
                <DisabledHint message={disabledHint}>
                    <div
                        style={{
                            ...styles.iconContainer,
                            ...(iconClickable && { cursor: 'pointer' }),
                        }}
                        {...(iconClickable && {
                            role: 'button',
                            tabIndex: 0,
                            title: iconTitle,
                            'aria-label': iconTitle,
                            onClick: () => OnClick(),
                            onKeyDown: (event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    OnClick();
                                }
                            },
                        })}
                    >
                        <img
                            src={image}
                            alt=""
                            style={{ maxHeight: '100%', maxWidth: '100%' }}
                        />
                    </div>
                </DisabledHint>
            </div>

            {
                showTime && (
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: '7px',
                        marginTop: '10px',

                    }}>
                        <span
                            style={{
                                height: "9px",
                                width: '9px',
                                flexShrink: 0,
                                borderRadius: '100%',
                                // Grey rather than green when there is no log, so
                                // the dot doesn't read as the device being live;
                                // amber when a reading has gone stale.
                                background: footer.dot,
                                display: 'inline-block'
                            }}></span>
                        <span style={{ ...styles.logText }} title={footer.title}>
                            {footer.text}
                            {footer.time && <span style={{ ...styles.logTime }}> {footer.time}</span>}
                            {footer.after && <span> · {footer.after}</span>}
                        </span>
                    </div>
                )
            }

        </div>
        // </Col>
    )
}

export default DetailContainer